<template>
  <div :class="containerClasses">
    <cdr-text
      v-if="!isComplete"
      tag="span"
      class="confirm-membership__subheading"
    >
      This card is just for REI Co-op Members, so we’ll need to
      confirm your membership information.
    </cdr-text>

    <!-- /* c8 ignore start */ -->
    <form
      novalidate
      class="confirm-membership__fields"
      @submit.prevent="$emit('form-submit')"
    >
      <cdr-input
        v-for="({ error, value, ...props }, index) in fields"
        :key="props.name"
        :aria-describedby="error && `${props.id}-${props.name}`"
        :disabled="props.disabled || disableFields"
        v-bind="props"
        :error="!!error"
        :model-value="value"
        class="confirm-membership__field"
        @input="event => handleInput({ index, event })"
        @blur="$emit('blur', index)"
      >
        <!-- /* c8 ignore end */ -->
        <template #info>
          <cdr-link
            v-if="props.name === 'memberNumber'"
            href="/membership/lookup"
            modifier="standalone"
            @click="handleForgotMemberClick"
          >
            Forgot Member Number
          </cdr-link>
        </template>
        <template #error>
          <span :id="`${props.id}-${props.name}`">{{ error }}</span>
        </template>
      </cdr-input>

      <div
        v-show="hasMemberValidationResponseError"
        class="confirm-membership__error"
      >
        <cdr-banner type="error">
          <template #icon-left>
            <icon-error-fill />
          </template>
          <p>
            <strong>
              Please check the information you entered.
            </strong>
            <a
              class="cdr-link"
              href="/membership/lookup"
            >
              Look up your member number
            </a>
            , or call
            <a
              class="cdr-link"
              data-analytics-id="look up member number call us link"
              href="tel:+1-800-426-4840"
            >
              1-800-426-4840
            </a>
            for help.
          </p>
        </cdr-banner>
      </div>

      <div :class="submitButtonClasses">
        <cdr-button
          :disabled="disableSubmit"
          type="submit"
        >
          <icon-check-lg v-if="isComplete" />
          {{ submitButtonText }}
        </cdr-button>
      </div>
    </form>

    <div
      v-if="!isComplete"
      class="confirm-membership__buy-now"
    >
      <div class="confirm-membership__buy-now-heading">
        <cdr-text tag="h4">
          This card’s just for members! Not an REI Co-op Member?
        </cdr-text>
        <cdr-tooltip
          id="member-only-tooltip"
          :auto-position="false"
          content-class="confirm-membership__buy-now-info"
          :open="true"
          :position="tooltipPosition"
        >
          <template #trigger>
            <cdr-button
              :icon-only="true"
              :with-background="false"
            >
              <template #icon>
                <icon-information-fill />
              </template>
              <span class="sr-only">more info</span>
            </cdr-button>
          </template>
          <div>
            For a one-time $30 fee, REI Co-op membership comes with a
            lifetime of benefits, including the opportunity to apply
            for the REI Co-op® Mastercard® which earns rewards on every purchase.
          </div>
        </cdr-tooltip>
      </div>
      <cdr-text class="confirm-membership__buy-now-content">
        To apply for this card, buy a membership for a one-time $30 fee.
        A welcome email—with your lifetime member number—will arrive within 30 minutes.
      </cdr-text>
      <add-membership-to-cart
        :is-added-to-cart="membershipAddedToCart"
        class="confirm-membership__buy-now-cta"
        data-analytics-config="exclude"
        modifier="link"
        @click="handleAddMembershipClick"
        @success="redirectToCart"
      >
        Buy a lifetime membership now
      </add-membership-to-cart>
    </div>
  </div>
</template>

<script>
import {
  CdrBanner,
  CdrButton,
  CdrInput,
  CdrLink,
  CdrText,
  CdrTooltip,
  IconCheckLg,
  IconErrorFill,
  IconInformationFill,
} from '@rei/cedar';
import AddMembershipToCart from '#components/add-membership-to-cart/AddMembershipToCart.vue';
import { metricsLink, metricsLinkAddToCart } from '#utils/analytics';

export default {
  name: 'ConfirmMembership',
  components: {
    AddMembershipToCart,
    CdrBanner,
    CdrButton,
    CdrInput,
    CdrLink,
    CdrText,
    CdrTooltip,
    IconErrorFill,
    IconCheckLg,
    IconInformationFill,
  },
  props: {
    isSmallScreen: {
      type: Boolean,
      default: false,
    },
    fields: {
      type: Array,
      required: true,
    },
    hasMemberValidationResponseError: {
      type: Boolean,
      required: true,
    },
    isComplete: {
      type: Boolean,
      required: true,
    },
    isSignedIn: {
      type: Boolean,
      required: true,
    },
    membershipAddedToCart: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['blur', 'form-submit', 'form-input'],
  computed: {
    containerClasses() {
      return ['confirm-membership', { 'confirm-membership--complete': this.isComplete }];
    },
    disableFields() {
      return !this.isSignedIn;
    },
    disableSubmit() {
      return this.disableFields || this.isComplete;
    },
    submitButtonText() {
      return this.isComplete ? 'Membership confirmed' : 'Confirm membership';
    },
    submitButtonClasses() {
      return {
        'confirm-membership__submit': true,
        'confirm-membership__submit--is-complete': this.isComplete,
      };
    },
    tooltipPosition() {
      const { isSmallScreen } = this;
      return isSmallScreen ? 'left' : 'top';
    },
  },
  methods: {
    redirectToCart() {
      if (typeof window !== 'undefined' && window.location) {
        window.location.href = '/ShoppingCart';
      }
    },
    /* c8 ignore start */
    handleInput({ index, event }) {
      const { value } = event.target;
      this.$emit('form-input', { index, value });
    },
    handleAddMembershipClick() {
      metricsLinkAddToCart('applet_Add Membership To Cart');
    },
    handleForgotMemberClick() {
      metricsLink('applet_Forgot Member Number');
    },
    /* c8 ignore end */
  },
};
</script>

<style lang="scss">
.confirm-membership--complete .confirm-membership__fields {
  padding-bottom: 0;
}

.confirm-membership__fields {
  padding-bottom: $cdr-space-one-x;
  display: grid;
  grid-row-gap: $cdr-space-one-x;
}

.confirm-membership__buy-now-info {
  @include cdr-sm-mq-down {
    max-width: 200px;
  }
}
/* spacing between inputs and error/submit divs is inconsistent */
.confirm-membership__field:nth-child(n + 2) {
  margin-top: -$cdr-space-half-x;
}

.confirm-membership__error {
  [class*=cdr-banner__message] {
    padding: $cdr-space-inset-one-x-squish;
  }
  p, a, strong {
    @include cdr-text-utility-sans-200;
  }
  strong {
    font-weight: bold;
  }
}

.confirm-membership__buy-now {
  display: grid;
  grid-gap: $cdr-space-half-x;
  position: relative;
  padding-top: $cdr-space-one-x;
  &::before {
    position: absolute;
    content: '';
    top: 0;
    right: 0;
    left: 0;
    margin: auto;
    transform: scaleX(1.5);
    border-bottom: 1px solid $cdr-color-border-primary;
  }

  @include cdr-md-mq-up {
    #member-only-tooltip {
      left: -200%;
    }
  }
}

.confirm-membership__buy-now-heading,
.confirm-membership__buy-now-content,
.confirm-membership__buy-now-cta {
  grid-column: 1
}

.confirm-membership__buy-now-heading {
  display: flex;
  align-items: center;
  h4 {
    @include cdr-text-heading-sans-200;
  }
}

.confirm-membership__subheading {
  display: inline-block;
  @include cdr-text-utility-sans-200;
}

.confirm-membership__submit--is-complete {
  button[disabled] {
    @include confirmed-disabled-button;
  }

  svg {
    height: 20px!important;
    width: 20px!important;
  }
}

.confirm-membership__buy-now-content {
  @include cdr-text-utility-sans-200;
}

.confirm-membership__buy-now-cta button {
  @include cdr-link-standalone-mixin;
  @include cdr-text-heading-sans-200;
  padding: 0;
}

.membership-amtc__notification {
  margin: $cdr-space-half-x auto 0 0;
}
</style>
